import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LIVE_CONNECTING, LIVE_OPEN, openLiveConnection, type LiveConnection } from '../application/liveConnection';
import { withLogWsTicket } from '../application/liveWs';
import { Api } from '../application/panelApi';
import { ExportCsvModal } from '../components/ExportCsvModal';
import { LaneIcon } from '../components/LaneIcon';
import { toast } from '../components/ToastHost';
import { setPendingServerFocus, takePendingLogCluster } from '../features/clusters';
import { toCsv, type CsvExport } from '../features/csvExport';
import { errText, nf, plural } from '../lib/format';
import { LVL } from '../lib/icons';
import { useApp } from '../store/AppContext';

type LogEntry = {
  t?: string;
  timestamp?: string;
  lvl?: string;
  text?: string;
  msg?: string;
  cluster?: string;
  source?: string;
  clusterId?: string;
  profile?: string;
  taskId?: string;
  group?: string;
  event?: string;
  searchEngine?: string;
};

type LogReport = {
  logs?: LogEntry[];
  total?: number;
  hits?: number;
  misses?: number;
  errors?: number;
  unavailable?: string[];
};

type TaskLike = { id: string; site?: string; searchEngine?: string };
type ClusterLike = { id: string; name?: string; os?: string };

type SourceKind = 'linux' | 'windows' | 'system';

const SOURCE_TITLE: Record<SourceKind, string> = {
  linux: 'Linux',
  windows: 'Windows',
  system: 'Панель управления',
};

type Period = 'live' | '24' | '168' | 'all';

type Filters = {
  q: string;
  task: string;
  level: string;
  group: string;
  cluster: string;
  period: Period;
};

type WsState = { ok: boolean; connecting: boolean; hadOpen: boolean };

type WsMessage = {
  type?: string;
  entry?: LogEntry;
  cluster?: string;
  source?: string;
};

const DEFAULT_FILTERS: Filters = {
  q: '',
  task: 'all',
  level: 'all',
  group: 'all',
  cluster: 'all',
  period: 'live',
};

const MAX_LOGS = 400;

const NO_LIVE_ADD = { total: 0, hits: 0, misses: 0, errors: 0 };

function normalizeLogTaskKey(value: unknown): string {
  return String(value || '')
    .toLowerCase()
    .replace(/^www\./, '')
    .replace(/[^a-z0-9]+/g, '');
}

function entryText(l: LogEntry): string {
  return l.text != null ? String(l.text) : String(l.msg ?? '').replace(/<[^>]*>/g, '');
}

function findTask(tasks: TaskLike[], key: string): TaskLike | undefined {
  const want = normalizeLogTaskKey(key);
  return tasks.find(
    (t) =>
      t.id === key ||
      t.site === key ||
      normalizeLogTaskKey(t.id) === want ||
      normalizeLogTaskKey(t.site) === want,
  );
}

function logTaskMatches(
  tasks: TaskLike[],
  selected: string,
  entryTaskId: string | undefined,
): boolean {
  if (!selected || selected === 'all') return true;
  if (!entryTaskId) return false;
  if (entryTaskId === selected) return true;
  const want = normalizeLogTaskKey(selected);
  const got = normalizeLogTaskKey(entryTaskId);
  if (want && got && want === got) return true;
  const task = tasks.find(
    (t) =>
      t.id === selected ||
      t.site === selected ||
      normalizeLogTaskKey(t.id) === want ||
      normalizeLogTaskKey(t.site) === want,
  );
  if (!task) return false;
  return (
    entryTaskId === task.id ||
    entryTaskId === task.site ||
    normalizeLogTaskKey(entryTaskId) === normalizeLogTaskKey(task.id) ||
    normalizeLogTaskKey(entryTaskId) === normalizeLogTaskKey(task.site)
  );
}

function resolveEntrySearchEngine(
  tasks: TaskLike[],
  entry: LogEntry,
): 'google' | 'bing' | undefined {
  if (entry.searchEngine === 'google' || entry.searchEngine === 'bing') return entry.searchEngine;

  if (!entry.taskId) return undefined;
  const task = findTask(tasks, entry.taskId);
  if (!task) return undefined;
  return task.searchEngine === 'google' ? 'google' : 'bing';
}

function logWsUrl(cluster: string): string {
  const url = new URL(
    `${location.protocol === 'https:' ? 'wss:' : 'ws:'}//${location.host}/api/logs/ws`,
  );
  if (cluster && cluster !== 'all') url.searchParams.set('cluster', cluster);
  return url.toString();
}

function formatEntryTime(l: LogEntry): string {
  const value = String(l.timestamp || l.t || '');
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value || 'Без даты' : date.toLocaleString('ru-RU');
}

function formatAuditTime(t: string): string {
  const d = new Date(t);
  return Number.isNaN(d.getTime()) ? t : d.toLocaleTimeString('ru-RU');
}

function shortEntryTime(l: LogEntry): string {
  const date = new Date(String(l.timestamp || l.t || ''));
  if (Number.isNaN(date.getTime())) return formatEntryTime(l);
  const time = date.toLocaleTimeString('ru-RU');
  if (date.toDateString() === new Date().toDateString()) return time;
  return `${date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })} ${time}`;
}

const LogRow = memo(function LogRow({
  l,
  taskLabel,
  source,
  serverName,
  onOpen,
}: {
  l: LogEntry;
  taskLabel?: string;
  source: SourceKind;
  serverName?: string;
  onOpen?: () => void;
}) {
  const level = ['ok', 'wa', 'er'].includes(String(l.lvl)) ? String(l.lvl) : '';
  const open = () => {
    if (window.getSelection()?.toString()) return;
    onOpen?.();
  };
  return (
    <div
      className={`logrow${level ? ` ${level}` : ''}${onOpen ? ' link' : ''}`}
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      title={onOpen && serverName ? `Открыть сервер ${serverName}` : undefined}
      onClick={onOpen ? open : undefined}
      onKeyDown={
        onOpen
          ? (e) => {
              if (e.key === 'Enter') onOpen();
            }
          : undefined
      }
    >
      <span className={`lsrc-ico ${source}`} title={SOURCE_TITLE[source]}>
        {source === 'system' ? (
          <LaneIcon kind="settings" />
        ) : (
          <img src={`/brand/${source}.png`} alt="" width={22} height={22} />
        )}
      </span>
      <time className="lt" title={formatEntryTime(l)}>
        {shortEntryTime(l)}
      </time>
      <div className="lbody">
        <div className="lm">{entryText(l)}</div>
        <div className="logmeta">
          {level ? <span className={`lv ${level}`}>{LVL[level]}</span> : null}
          <span className="lsrc">{l.cluster || l.source || 'Control'}</span>
          {taskLabel ? <span className="ltask">{taskLabel}</span> : null}
          {l.profile && l.profile !== '—' ? <span className="lp">профиль {l.profile}</span> : null}
        </div>
      </div>
    </div>
  );
});

const PERIODS: Array<[Period, string]> = [
  ['live', 'Сейчас'],
  ['24', '24 часа'],
  ['168', '7 дней'],
  ['all', 'Всё время'],
];

const LEVELS: Array<[string, string]> = [
  ['all', 'Все'],
  ['ok', 'Успех'],
  ['wa', 'Внимание'],
  ['er', 'Ошибки'],
];

export function LogsPage() {
  const {
    tasks: rawTasks,
    clusters: rawClusters,
    controlMode,
    auditLog,
    pendingLogTaskId,
    setPendingLogTaskId,
    refreshTasks,
    refreshClusters,
    ui,
    goto,
  } = useApp();
  const tasks = rawTasks as TaskLike[];
  const clusters = rawClusters as ClusterLike[];

  const [filters, setFilters] = useState<Filters>(() => {
    if (pendingLogTaskId) return { ...DEFAULT_FILTERS, task: pendingLogTaskId, period: 'all' };
    const cluster = takePendingLogCluster();
    return cluster ? { ...DEFAULT_FILTERS, cluster } : DEFAULT_FILTERS;
  });
  const [qDebounced, setQDebounced] = useState(filters.q);
  const [paused, setPaused] = useState(false);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [report, setReport] = useState<LogReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [logError, setLogError] = useState<string | null>(null);
  const [ws, setWs] = useState<WsState>({ ok: false, connecting: false, hadOpen: false });
  const [tasksFresh, setTasksFresh] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const [tab, setTab] = useState<'feed' | 'audit'>('feed');
  const [exp, setExp] = useState<CsvExport | null>(null);

  const [liveAdd, setLiveAdd] = useState(NO_LIVE_ADD);

  const requestRef = useRef(0);
  const pendingLive = useRef<LogEntry[]>([]);
  const rafRef = useRef<number | null>(null);
  const matchRef = useRef<(entry: LogEntry) => boolean>(() => true);

  const live = filters.period === 'live';


  useEffect(() => {
    if (!pendingLogTaskId) return;
    setFilters({ ...DEFAULT_FILTERS, task: pendingLogTaskId, period: 'all' });
    setQDebounced('');
    setLogs([]);
    setReport(null);
    setPendingLogTaskId(null);
  }, [pendingLogTaskId, setPendingLogTaskId]);


  useEffect(() => {
    let alive = true;
    void Promise.all([
      refreshClusters().catch(() => undefined),
      refreshTasks().catch(() => undefined),
    ]).then(() => {
      if (alive) setTasksFresh(true);
    });
    return () => {
      alive = false;
    };
  }, [refreshClusters, refreshTasks]);


  useEffect(() => {
    const timer = window.setTimeout(() => setQDebounced(filters.q), 300);
    return () => window.clearTimeout(timer);
  }, [filters.q]);

  const engineFilter = ui.scope;
  const engineTasks = useMemo(
    () => tasks.filter((t) => (t.searchEngine || 'bing') === engineFilter),
    [tasks, engineFilter],
  );


  useEffect(() => {
    if (!tasksFresh || filters.task === 'all') return;
    if (!engineTasks.some((t) => t.id === filters.task)) setFilters((f) => ({ ...f, task: 'all' }));
  }, [tasksFresh, engineTasks, filters.task]);

  const patchFilters = useCallback((patch: Partial<Filters>) => {

    if ('cluster' in patch || 'period' in patch) {
      pendingLive.current = [];
      setLogs([]);
      setReport(null);
    }
    setFilters((f) => ({ ...f, ...patch }));
  }, []);


  const { task, level, group, cluster, period } = filters;
  useEffect(() => {
    const req = ++requestRef.current;
    const params = new URLSearchParams({ limit: String(MAX_LOGS), clusterId: cluster || 'all' });
    const selectedTask = task.trim();
    if (selectedTask && selectedTask !== 'all') params.set('taskId', selectedTask);
    params.set('searchEngine', engineFilter);
    const q = qDebounced.trim();
    if (q) params.set('q', q);
    if (level !== 'all') params.set('level', level);
    if (group !== 'all') params.set('group', group);
    if (period !== 'live') {
      params.set('history', 'true');
      if (period !== 'all')
        params.set('since', new Date(Date.now() - Number(period) * 3600000).toISOString());
      params.set('until', new Date().toISOString());
    }
    setReport(null);
    setLogError(null);
    setLoading(true);
    void (async () => {
      try {
        const data = (await Api.logs.report(params.toString())) as LogReport;
        if (req !== requestRef.current) return;
        setLogs(data.logs || []);
        setReport(data);
        setLiveAdd(NO_LIVE_ADD);
      } catch (err) {
        if (req !== requestRef.current) return;
        setReport(null);
        setLogs([]);
        setLogError(errText(err));
        toast(errText(err), 'err');
      } finally {
        if (req === requestRef.current) setLoading(false);
      }
    })();
  }, [task, level, group, cluster, period, engineFilter, qDebounced, reloadTick]);

  useEffect(() => {
    if (period !== 'live' || paused || ws.ok || ws.connecting) return;
    const timer = window.setInterval(() => setReloadTick((n) => n + 1), 4000);
    return () => window.clearInterval(timer);
  }, [period, paused, ws.ok]);


  useEffect(() => {
    if (period !== 'live' || paused) {
      setWs({ ok: false, connecting: false, hadOpen: false });
      return;
    }
    let disposed = false;
    let sock: LiveConnection | null = null;
    let retryTimer: number | undefined;
    let pingTimer: number | undefined;
    let watchTimer: number | undefined;

    const flush = () => {
      rafRef.current = null;
      if (!pendingLive.current.length) return;
      const batch = pendingLive.current;
      pendingLive.current = [];
      const newestFirst = batch.slice().reverse();
      setLogs((prev) => [...newestFirst, ...prev].slice(0, MAX_LOGS));
      const matched = batch.filter((e) => matchRef.current(e));
      if (matched.length) {
        setLiveAdd((prev) => ({
          total: prev.total + matched.length,
          hits: prev.hits + matched.filter((e) => e.event === 'target_completed').length,
          misses: prev.misses + matched.filter((e) => e.event === 'target_miss').length,
          errors: prev.errors + matched.filter((e) => e.lvl === 'er').length,
        }));
      }
    };

    const detach = () => {
      window.clearTimeout(watchTimer);
      window.clearInterval(pingTimer);
      if (sock) {
        sock.onopen = null;
        sock.onmessage = null;
        sock.onclose = null;
        sock.onerror = null;
        try {
          sock.close();
        } catch {

        }
        sock = null;
      }
    };

    const scheduleReconnect = () => {
      if (disposed) return;
      window.clearTimeout(retryTimer);
      retryTimer = window.setTimeout(connect, 3000);
    };

    function connect() {
      if (disposed) return;
      detach();
      setWs((p) => ({ ...p, ok: false, connecting: true }));
      void withLogWsTicket(logWsUrl(cluster)).then((url) => {
        if (disposed) return;
        openSock(url);
      });
    }

    function openSock(url: string) {
      let opened = false;
      let s: LiveConnection;
      try {
        s = openLiveConnection(url);
      } catch {
        setWs((p) => ({ ...p, ok: false, connecting: false }));
        scheduleReconnect();
        return;
      }
      sock = s;
      watchTimer = window.setTimeout(() => {
        if (!opened && s.readyState === LIVE_CONNECTING) {
          try {
            s.close();
          } catch {

          }
        }
      }, 20_000);
      s.onopen = () => {
        opened = true;
        window.clearTimeout(watchTimer);
        setWs({ ok: true, connecting: false, hadOpen: true });
        window.clearInterval(pingTimer);
        pingTimer = window.setInterval(() => {
          if (s.readyState !== LIVE_OPEN) return;
          try {
            s.send(JSON.stringify({ type: 'ping', ts: Date.now() }));
          } catch {

          }
        }, 25000);
      };
      s.onmessage = (ev) => {
        try {
          const msg = JSON.parse(String(ev.data)) as WsMessage;
          if (msg.type === 'ping' || msg.type === 'pong' || msg.type === 'hello') {
            setWs((p) => (p.ok ? p : { ...p, ok: true }));
            if (msg.type === 'ping' && s.readyState === LIVE_OPEN) {
              s.send(JSON.stringify({ type: 'pong', ts: Date.now() }));
            }
            return;
          }
          if (msg.type === 'log' && msg.entry) {
            const entry: LogEntry = {
              ...msg.entry,
              cluster: msg.cluster || msg.source || msg.entry.cluster,
            };
            pendingLive.current.push(entry);
            if (rafRef.current == null) rafRef.current = requestAnimationFrame(flush);
          }
        } catch {

        }
      };
      s.onclose = () => {
        window.clearTimeout(watchTimer);
        window.clearInterval(pingTimer);
        if (sock === s) sock = null;
        setWs((p) => ({ ...p, ok: false, connecting: false }));
        scheduleReconnect();
      };
      s.onerror = () => undefined;
    }

    setWs({ ok: false, connecting: true, hadOpen: false });
    connect();

    return () => {
      disposed = true;
      window.clearTimeout(retryTimer);
      detach();
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      pendingLive.current = [];
    };
  }, [period, paused, cluster]);

  const matchEntry = useCallback(
    (entry: LogEntry) => {
      const q = filters.q.trim().toLowerCase();
      const clusterName = clusters.find((c) => c.id === filters.cluster)?.name;
      if (!logTaskMatches(tasks, filters.task, entry.taskId)) return false;
      if (engineFilter === 'google' || engineFilter === 'bing') {
        const entryEng = resolveEntrySearchEngine(tasks, entry);
        if (entryEng && entryEng !== engineFilter) return false;
      }
      if (filters.level !== 'all' && entry.lvl !== filters.level) return false;
      if (filters.group !== 'all' && entry.group !== filters.group) return false;
      if (
        q &&
        !`${entry.text || entry.msg || ''} ${entry.profile || ''} ${entry.taskId || ''} ${entry.event || ''}`
          .toLowerCase()
          .includes(q)
      ) {
        return false;
      }
      if (filters.cluster !== 'all') {
        if (entry.clusterId !== filters.cluster && !(clusterName && entry.cluster === clusterName))
          return false;
      }
      return true;
    },
    [tasks, clusters, filters, engineFilter],
  );
  matchRef.current = matchEntry;

  const rows = useMemo(() => logs.filter(matchEntry), [logs, matchEntry]);

  const total = report ? (report.total ?? 0) + liveAdd.total : rows.length;
  const hits = report
    ? (report.hits ?? 0) + liveAdd.hits
    : rows.filter((l) => l.event === 'target_completed').length;
  const misses = report
    ? (report.misses ?? 0) + liveAdd.misses
    : rows.filter((l) => l.event === 'target_miss').length;
  const errors = report
    ? (report.errors ?? 0) + liveAdd.errors
    : rows.filter((l) => l.lvl === 'er').length;

  const missing = report?.unavailable?.length
    ? `Часть серверов не ответила, данные неполные: ${report.unavailable.join('; ')}`
    : '';
  let summary = '';
  if (logError) summary = logError;
  else if (loading && !live) summary = 'Загружаем…';
  else if (report || ws.ok) summary = `Показано ${nf.format(rows.length)} из ${nf.format(total)}`;

  let streamText: string;
  let streamOff = true;
  if (!live) streamText = 'История';
  else if (paused) streamText = 'На паузе';
  else if (ws.ok) {
    streamText = 'В реальном времени';
    streamOff = false;
  } else if (ws.hadOpen && !ws.connecting) streamText = 'Переподключение…';
  else streamText = 'Подключение…';

  const togglePause = () => {
    const next = !paused;
    if (next) {
      ++requestRef.current;
      pendingLive.current = [];
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      setLoading(false);
    }
    setPaused(next);
    toast(
      next
        ? 'Поток событий на паузе. Нажмите «Продолжить», чтобы снова получать новые записи.'
        : 'Поток событий возобновлён.',
    );
  };

  const copyLogs = async () => {
    const text = rows
      .map(
        (l) =>
          `${l.t ?? l.timestamp ?? ''} [${LVL[l.lvl ?? ''] || LVL['']}] профиль ${l.profile ?? ''} — ${entryText(l)}`,
      )
      .join('\n');
    try {
      await navigator.clipboard.writeText(text);
      toast(`Скопировано в буфер обмена строк: ${rows.length}.`, 'ok');
    } catch {
      toast(
        'Не удалось скопировать: браузер не дал доступ к буферу обмена. Нажмите «Экспорт CSV» и скопируйте текст оттуда.',
        'err',
      );
    }
  };

  const exportLogs = () =>
    setExp({
      name: 'logs.csv',
      csv: toCsv(
        ['Время', 'Уровень', 'Профиль', 'Событие'],
        rows.map((l) => [l.t ?? l.timestamp, LVL[l.lvl ?? ''] || LVL[''], l.profile, entryText(l)]),
      ),
    });

  const exportAudit = () =>
    setExp({
      name: 'audit.csv',
      csv: toCsv(
        ['Время', 'Пользователь', 'Действие', 'Объект'],
        auditLog.map((a) => [a.t, a.user, a.act, a.obj]),
      ),
    });

  const clearFilters = () => {
    pendingLive.current = [];
    setLogs([]);
    setReport(null);
    setQDebounced('');
    setFilters(DEFAULT_FILTERS);
  };

  const isControl = controlMode === 'control';
  const taskKnown = engineTasks.some((t) => t.id === filters.task);
  const dirty = JSON.stringify(filters) !== JSON.stringify(DEFAULT_FILTERS);
  const taskLabel = (key?: string) => (key ? findTask(tasks, key)?.site || key : undefined);
  const nodeOf = (l: LogEntry) =>
    clusters.find((c) => c.id === l.clusterId) ??
    clusters.find((c) => c.name && c.name === l.cluster);
  const sourceOf = (l: LogEntry): SourceKind => {
    const node = nodeOf(l);
    if (!node) return 'system';
    return node.os === 'windows' ? 'windows' : 'linux';
  };
  const openServer = (id: string) => {
    setPendingServerFocus(id);
    goto('servers');
  };

  return (
    <section id="page-logs" className="sv lg">
      <div className="sv-head">
        <h1 className="h1-lane">
          <LaneIcon kind="logs" />
          Логи
        </h1>
        <div className="lp-seg" id="logTabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'feed'}
            className={tab === 'feed' ? 'on' : undefined}
            data-tab="feed"
            onClick={() => setTab('feed')}
          >
            События
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'audit'}
            className={tab === 'audit' ? 'on' : undefined}
            data-tab="audit"
            onClick={() => setTab('audit')}
          >
            Действия в панели
          </button>
        </div>
      </div>

      {tab === 'feed' ? (
        <>
          <div className="sv-summary">
            <div className="sv-stat">
              <span className="sv-k">Событий</span>
              <b id="lgCount">{nf.format(total)}</b>
            </div>
            <div className="sv-stat">
              <span className="sv-k">Засчитано кликов</span>
              <b id="lgHit" className="ok">
                {nf.format(hits)}
              </b>
            </div>
            <div className="sv-stat">
              <span className="sv-k">Промахов</span>
              <b id="lgMiss" className="warn">
                {nf.format(misses)}
              </b>
            </div>
            <div className="sv-stat">
              <span className="sv-k">Ошибок</span>
              <b id="lgErr" className="err">
                {nf.format(errors)}
              </b>
            </div>
          </div>

          <div className="lg-filters">
            <div className="lg-row">
              <input
                type="text"
                id="logFilter"
                className="lg-search"
                placeholder="Поиск по тексту, сайту или номеру профиля"
                spellCheck={false}
                value={filters.q}
                onChange={(e) => patchFilters({ q: e.target.value })}
              />
              <div className="lp-seg" id="logPeriod" role="radiogroup" aria-label="Период">
                {PERIODS.map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    role="radio"
                    aria-checked={filters.period === id}
                    className={filters.period === id ? 'on' : undefined}
                    onClick={() => patchFilters({ period: id })}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="lg-row">
              <select
                id="logCluster"
                value={filters.cluster}
                onChange={(e) => patchFilters({ cluster: e.target.value })}
              >
                {isControl ? (
                  <>
                    <option value="all">Все сервера</option>
                    <option value="control">Панель управления</option>
                    {clusters.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name ?? c.id}
                      </option>
                    ))}
                  </>
                ) : (
                  <option value="all">Локальный воркер</option>
                )}
              </select>
              <select
                id="logTask"
                value={filters.task}
                onChange={(e) => patchFilters({ task: e.target.value })}
              >
                <option value="all">Все задачи</option>
                {filters.task !== 'all' && !taskKnown ? (
                  <option value={filters.task}>{filters.task}</option>
                ) : null}
                {engineTasks.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.site || t.id}
                  </option>
                ))}
              </select>
              <select
                id="logGroup"
                value={filters.group}
                onChange={(e) => patchFilters({ group: e.target.value })}
              >
                <option value="all">Все типы</option>
                <option value="farm">Подготовка профилей</option>
                <option value="target">Клики по задачам</option>
                <option value="system">Система</option>
              </select>
              <div className="lp-seg" id="logLevel" role="radiogroup" aria-label="Уровень">
                {LEVELS.map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    role="radio"
                    aria-checked={filters.level === id}
                    className={`${filters.level === id ? 'on' : ''} lv-${id}`.trim()}
                    onClick={() => patchFilters({ level: id })}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {dirty ? (
                <button
                  type="button"
                  className="lp-link lg-reset"
                  id="logClear"
                  onClick={clearFilters}
                >
                  Сбросить фильтры
                </button>
              ) : null}
            </div>
          </div>

          <div className="lg-feed">
            <div className="lg-feed-head">
              <span className={`live${streamOff ? ' off' : ''}`} id="logLive">
                <i></i>
                {streamText}
              </span>
              {summary ? (
                <span
                  className={`lg-summary${logError ? ' err' : ''}`}
                  id="logSummary"
                  role="status"
                >
                  {summary}
                </span>
              ) : null}
              <div className="lg-feed-actions">
                {live ? (
                  <button type="button" className="btn" id="logPause" onClick={togglePause}>
                    {paused ? 'Продолжить' : 'Пауза'}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn"
                    id="logAnalyze"
                    onClick={() => setReloadTick((n) => n + 1)}
                  >
                    Обновить
                  </button>
                )}
                <button type="button" className="btn" id="logCopy" onClick={() => void copyLogs()}>
                  Скопировать
                </button>
                <button type="button" className="btn" data-export="logs" onClick={exportLogs}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3v11m-4-4 4 4 4-4M4 16v4h16v-4" /></svg>
                  Экспорт CSV
                </button>
              </div>
            </div>
            {missing ? <div className="lg-missing">{missing}</div> : null}
            <div className="logfeed" id="logFeed">
              {rows.length ? (
                rows.map((l, i) => (
                  <LogRow
                    key={`${l.timestamp ?? l.t ?? ''}-${l.taskId ?? ''}-${i}`}
                    l={l}
                    taskLabel={taskLabel(l.taskId)}
                    source={sourceOf(l)}
                    serverName={nodeOf(l)?.name}
                    onOpen={nodeOf(l) ? () => openServer(nodeOf(l)!.id) : undefined}
                  />
                ))
              ) : (
                <div className="empty">Нет событий по выбранным фильтрам</div>
              )}
            </div>
          </div>
        </>
      ) : (
        <div className="lg-feed">
          <div className="lg-feed-head">
            <span className="lg-summary">
              {nf.format(auditLog.length)}{' '}
              {plural(auditLog.length, 'действие', 'действия', 'действий')}
            </span>
            <div className="lg-feed-actions">
              <button type="button" className="btn" data-export="audit" onClick={exportAudit}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3v11m-4-4 4 4 4-4M4 16v4h16v-4" /></svg>
                Экспорт CSV
              </button>
            </div>
          </div>
          <div className="scroll">
            <table className="lg-audit">
              <thead>
                <tr>
                  <th>Время</th>
                  <th>Пользователь</th>
                  <th>Действие</th>
                  <th>Объект</th>
                </tr>
              </thead>
              <tbody id="auditBody">
                {auditLog.length ? (
                  auditLog.map((a, i) => (
                    <tr key={`${a.t}-${i}`}>
                      <td data-l="Время" className="lg-audit-time">
                        {formatAuditTime(a.t)}
                      </td>
                      <td data-l="Пользователь">
                        <span className="tag n">{a.user}</span>
                      </td>
                      <td data-l="Действие">{a.act}</td>
                      <td data-l="Объект" className="lg-audit-obj">
                        {a.obj || '—'}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4}>
                      <div className="empty">Действий пока не было</div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {exp ? <ExportCsvModal name={exp.name} csv={exp.csv} onClose={() => setExp(null)} /> : null}
    </section>
  );
}
